import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core'
import {
  FormBuilder,
} from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { AppointmentAuthorizationService, AppointmentModel, AUTHORIZATION_STATUS, AuthorizationModel, AuthorizationStatusPullData, AvailityServiceReview, CustomFieldService, filterAction, InsuranceProviderConfiguration, InsuranceProviderConfigurationService, InsuranceProviderLookupsService, Patient, PatientInsurance, PatientPalRestError, PortalModel, PortalQuestion, SaveAuthorizationParams, updateFormValidity, WebsocketAction, WebSocketService } from '@mmx/shared'
import { TranslateService } from '@ngx-translate/core'
import { filter as _filter, flatten, uniq } from 'lodash'
import * as moment from 'moment-timezone'
import { BehaviorSubject, combineLatest, distinctUntilKeyChanged, filter, finalize, map, of, shareReplay, startWith, Subject, switchMap, takeUntil, withLatestFrom } from 'rxjs'

import { ElementFormData } from '../prior-authorization-form'
import { PendingAuthorizationAttachment } from './components/auth-attachments'

export interface AuthorizationDialogData {
  authorization?: AuthorizationModel
  patient: Patient
  patientInsurances: PatientInsurance[]
  appointment: AppointmentModel
  timezone: string
}

@Component({
  selector: 'mmx-authorization-dialog',
  templateUrl: './authorization-dialog.component.html',
  styleUrls: ['./authorization-dialog.component.scss'],
  standalone: false,
})
export class AuthorizationDialogComponent implements OnInit, OnDestroy {
  public loadingAnswers$ = new BehaviorSubject<boolean>(false)
  public currentAnswerStep$ = new BehaviorSubject<string>(null)
  public pendingAttachments$ = new BehaviorSubject<PendingAuthorizationAttachment[]>([])
  public statusPullData: AuthorizationStatusPullData | undefined
  private onDestroy$ = new Subject<void>()

  authForm = this.fb.group({
    details: this.fb.control<Partial<SaveAuthorizationParams>>(null),
    clinicalInformation: this.fb.control<Record<string, any>>(null),
  })

  submissionForm = this.fb.group({
    priorAuthorization: this.fb.control<Record<string, any>>(null),
  })

  loadAttachments$ = new Subject<string>()
  attachments$ = this.loadAttachments$.pipe(
    switchMap((authorizationId) => this.authorizationSvc.getAttachments(authorizationId).pipe(
      finalize(() => this.attachmentsLoading$.next(false)),
    )),
    map(({ data }) => data),
    shareReplay(1),
  )
  attachmentsLoading$ = new BehaviorSubject(false)
  saving$ = new BehaviorSubject(false)
  includeUnits = false
  outdated = false
  ignoredOutdatedConflicts = false
  outdatedWarnings: string[] = []
  availablePortals: PortalModel[] = []
  formData: ElementFormData

  public get isAuthSubmissionSupported() {
    return this.data.authorization?.status === AUTHORIZATION_STATUS.NOT_INITIATED
  }

  public submitToPayerDisabled$ = combineLatest([
    this.submissionForm.statusChanges.pipe(
      startWith(this.submissionForm.status),
    ),
    this.saving$,
  ])
    .pipe(
      map(([status, saving]) => status !== 'VALID' || saving),
    )

  public procedureCodes$ = new BehaviorSubject<string[]>([])
  public diagnosisCodes$ = new BehaviorSubject<string[]>([])

  public allQuestions$ = new BehaviorSubject<PortalQuestion[]>([])

  public questions$ = combineLatest([
    this.allQuestions$,
    this.authForm.get('details').valueChanges.pipe(
      startWith(this.authForm.get('details').value),
    ),
  ]).pipe(
    map(([allQuestions]) => {
      const details = this.authForm.get('details').value
      const { procedureCodes = [], diagnosisCodes = [] } = details || {}

      if (!procedureCodes.length && !diagnosisCodes.length) {
        return allQuestions
      }

      return allQuestions.filter((question) => {
        if (procedureCodes.length && question.procedureCodes?.length && !question.procedureCodes?.some((code) => procedureCodes.includes(code))) {
          return false
        }

        if (diagnosisCodes.length && question.diagnosesCodes?.length && !question.diagnosesCodes?.some((code) => diagnosisCodes.includes(code))) {
          return false
        }

        return true
      })
    }),
  )

  constructor(
    public dialogRef: MatDialogRef<AuthorizationDialogComponent>,
    public customFieldsService: CustomFieldService,
    public insuranceProviderConfigurationService: InsuranceProviderConfigurationService,
    @Inject(MAT_DIALOG_DATA) public data: AuthorizationDialogData,
    private fb: FormBuilder,
    private authorizationSvc: AppointmentAuthorizationService,
    private insuranceProviderLookupsService: InsuranceProviderLookupsService,
    private websocketService: WebSocketService,
    private cdr: ChangeDetectorRef,
    private snack: MatSnackBar,
    private t: TranslateService,
  ) { }

  public ngOnInit() {
    const { authorization, appointment } = this.data

    if (appointment) {
      this.procedureCodes$.next(_filter(uniq(flatten(appointment.products.map(product => product.codes)))))
      this.diagnosisCodes$.next(_filter(uniq(flatten(appointment.studies?.map(study => study.diagnosisCodes?.map(diagnosis => diagnosis.code))))))
    }

    if (!authorization) {
      this.authForm.get('details').patchValue({
        status: AUTHORIZATION_STATUS.NOT_INITIATED,
        procedureCodes: this.procedureCodes$.getValue(),
        diagnosisCodes: this.diagnosisCodes$.getValue(),
        patientId: appointment?.patientId,
        facilityId: appointment?.locationId,
      })

      updateFormValidity(this.authForm, false)
      return
    }

    this.setup(authorization)

    this.authForm.get('details').valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        startWith(this.authForm.get('details').value),
        distinctUntilKeyChanged('carrierLookupId'),
        filter(({ carrierLookupId }) => !!carrierLookupId),
        switchMap(({ carrierLookupId }) => this.insuranceProviderLookupsService.get(carrierLookupId)),
      )
      .subscribe((providerLookup) => {
        this.insuranceProviderConfigurationService.configurationParams$.next({
          providerLookupId: providerLookup.id,
          payerId: providerLookup.provider?.slug,
          type: InsuranceProviderConfiguration.TYPE.SERVICE_REVIEWS,
          subtypeId: InsuranceProviderConfiguration.SUBTYPE_ID.HEALTH_SERVICES,
        })

        this.formData = {
          patient: this.data.patient,
          carrier: providerLookup,
          appointment: this.data.appointment,
          patientInsurance: this.data.patientInsurances.find((insurance) => insurance.carrierLookupId === providerLookup.id),
          authorization: this.data.authorization,
        }
      })

    this.websocketService.clinicianMessage$
      .pipe(
        takeUntil(this.onDestroy$),
        filterAction(WebsocketAction.PRIOR_AUTHORIZATION_SUBMIT),
      ).subscribe((response) => {
        this.saving$.next(false)

        if (response.data?.errors || (response.data as AvailityServiceReview.Review).validationMessages) {
          this.cdr.detectChanges()
          this.snack.open(this.t.instant('prior-authorization.form.submit-fail'))
          return
        }

        this.snack.open(this.t.instant('prior-authorization.form.submit-success'))
        this.dialogRef.close()
      })
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public ignoreOutdatedConflicts(): void {
    this.outdated = false
    this.ignoredOutdatedConflicts = true
  }

  public onPendingAttachments(attachments: PendingAuthorizationAttachment[]) {
    this.pendingAttachments$.next(attachments)
  }

  public onNewAttachmentsAdded() {
    if (!this.data.authorization?.id) {
      return
    }

    this.attachmentsLoading$.next(true)
    this.loadAttachments$.next(this.data.authorization.id)
  }

  public onPullStatus() {
    this.saving$.next(true)
    this.websocketService.clinicianMessage$
      .pipe(
        takeUntil(this.onDestroy$),
        filterAction(WebsocketAction.PRIOR_AUTHORIZATION_STATUS),
      )
      .subscribe((response) => {
        this.saving$.next(false)
        if (response.error) {
          this.snack.open(this.t.instant('prior-authorization.form.pull-status-fail'), null, { duration: 5000 })
          return
        }

        this.snack.open(this.t.instant('prior-authorization.form.pull-status-success'), null, { duration: 5000 })
        if (response.data.authorization) {
          this.setup(new AuthorizationModel(response.data.authorization))
        }
      })

    this.websocketService.messageQueue.next({
      action: WebsocketAction.PRIOR_AUTHORIZATION_STATUS,
      authorizationId: this.formData.authorization.id,
    })
  }

  public onSubmitToPayer(): void {
    updateFormValidity(this.submissionForm)

    if (this.submissionForm.invalid) {
      return
    }

    this.saving$.next(true)
    const formValue = this.submissionForm.value.priorAuthorization
    const providerLookup = this.formData.carrier
    const serviceReview = {
      payer: {
        id: providerLookup.provider?.slug,
      },
      ...formValue,
    }

    this.websocketService.messageQueue.next({
      action: WebsocketAction.PRIOR_AUTHORIZATION_SUBMIT,
      authorizationId: this.formData.authorization.id,
      serviceReview,
      providerLookupId: providerLookup.id,
      providerId: providerLookup.provider?.id,
    })
  }

  public async onSubmit() {
    updateFormValidity(this.authForm)

    if (this.authForm.invalid) {
      return
    }

    this.saving$.next(true)

    this.handleSubmit()
      .pipe(
        takeUntil(this.onDestroy$),
        withLatestFrom(this.pendingAttachments$),
        switchMap(([authorization, pendingAttachments]) => {
          if (!pendingAttachments.length) {
            return of(authorization)
          }

          return this.authorizationSvc.uploadAuthAttachments(authorization.id, pendingAttachments)
        }),
      )
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (err: PatientPalRestError) => {
          this.saving$.next(false)
          alert(err.error.message)
        },
      })
  }

  private handleSubmit() {
    const { startAt, endAt, facility, ...formValue } = this.authForm.get('details').value
    const { questions } = this.authForm.get('clinicalInformation').value || {}

    const ignoredOutdatedIds = this.data.authorization?.ignoredOutdatedIds ?? new Set()
    if (this.ignoredOutdatedConflicts) {
      ignoredOutdatedIds.add(this.data.appointment.id)
    }

    const appointmentIds = this.data.authorization?.appointmentIds ?? new Set()
    appointmentIds.add(this.data.appointment.id)

    const params = {
      ...formValue,
      facilityId: facility?.id,
      facilityNPI: facility?.provider?.id,
      startAt,
      endAt,
      appointmentIds: [...appointmentIds],
      ignoredOutdatedIds: [...ignoredOutdatedIds],
      patientId: this.data.appointment.patientId,
      portalData: questions,
    } as SaveAuthorizationParams

    return this.data.authorization
      ? this.authorizationSvc.updateAuthorization(this.data.authorization.id, params)
      : this.authorizationSvc.createAuthorization(params)
  }

  private setup(authorization: AuthorizationModel) {
    this.outdatedWarnings = []
    const { appointment } = this.data
    const authorizationData = authorization.toJSON()
    const startAt = authorization.dateFrom ? moment.tz(authorization.dateFrom, this.data.timezone) : undefined
    const endAt = authorization.dateTo ? moment.tz(authorization.dateTo, this.data.timezone) : undefined

    this.authForm.get('details').patchValue({
      ...authorizationData,
      startAt,
      endAt,
      procedureCodes: authorization.procedureCodes || this.procedureCodes$.getValue(),
      diagnosisCodes: authorization.diagnosisCodes || this.diagnosisCodes$.getValue(),
      customFields: authorization.customFields,
      // if the authorization does not have a facility, default it to the
      // appointment's location this is a bit strange but facility is a required
      // field so we shouldn't have many situations without a facility. Overall,
      // this provides a smoother user experience
      facility: authorization.facilityId ?? this.data.appointment.locationId,
    })

    updateFormValidity(this.authForm, false)

    if (authorization.limitations) {
      this.includeUnits = true
    }

    this.outdated = authorization.outdatedAppointmentIds.has(appointment.id) && !authorization.ignoredOutdatedIds.has(appointment.id)

    this.authorizationSvc.getAuthorization(authorization.id, appointment.id)
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((extendedAuth) => {
        this.attachmentsLoading$.next(true)
        this.loadAttachments$.next(extendedAuth.id)

        if (extendedAuth.outdatedFlags) {
          for (const flag of extendedAuth.outdatedFlags) {
            this.outdatedWarnings.push(`dialog.authorization.outdated-warning.${flag}`)
          }
        }

        if (extendedAuth.portals) {
          this.availablePortals = extendedAuth.portals
        }

        if (extendedAuth.id) {
          this.authForm.get('clinicalInformation').patchValue({
            id: extendedAuth.id,
          })
        }

        if (extendedAuth.questions) {
          this.allQuestions$.next(extendedAuth.questions)
          this.authForm.get('clinicalInformation').patchValue({
            questions: extendedAuth.portalData?.questionAnswers,
          })
        }

        this.statusPullData = extendedAuth.statusPullData

        this.cdr.detectChanges()
      })
  }
}
